/**
 * Pure helpers for "Who should this go to?" and the routing register (16 Oct).
 * No React, no i18n instance: the node tests import this file directly.
 */

/** The reassign pick-list, in the order the form shows it. */
export const REASSIGN_REASONS = [
  "wrong_team",
  "needs_specialist",
  "out_of_area",
  "workload",
  "customer_asked",
  "other",
] as const;
export type ReassignReasonCode = (typeof REASSIGN_REASONS)[number];

/** Reason codes the page knows a sentence for; anything else is shown as its code. */
export const REASON_CODES = [
  "register_rule",
  "offering_map",
  "system_map",
  "history_share",
  "history_moved_away",
  "outcome_team",
  "helpdesk_moves",
  "ai_reading",
  "site_owner",
  "load_tiebreak",
] as const;
export type ReasonCode = (typeof REASON_CODES)[number];

export const PERSON_REASON_CODES = [
  "register_lead",
  "register_owner",
  "register_backup",
  "outcome_owner",
  "site_owner_person",
  "owner_history",
] as const;
export type PersonReasonCode = (typeof PERSON_REASON_CODES)[number];

export function isReasonCode(code: string): code is ReasonCode {
  return (REASON_CODES as readonly string[]).includes(code);
}

export function isPersonReasonCode(code: string): code is PersonReasonCode {
  return (PERSON_REASON_CODES as readonly string[]).includes(code);
}

export function isReassignReason(code: string): code is ReassignReasonCode {
  return (REASSIGN_REASONS as readonly string[]).includes(code);
}

/**
 * What the dispatcher typed: a ticket number ("10231", "#10231", "SR-4471")
 * or text to read (anything else, e.g. Ivanti's summary).
 */
export function parseQuery(input: string): { ticketNumber: string } | { text: string } | null {
  const trimmed = input.trim();
  if (trimmed === "") {
    return null;
  }
  if (/^#?[A-Za-z]{0,4}-?\d{1,12}$/.test(trimmed)) {
    return { ticketNumber: trimmed.replace(/^#/, "") };
  }
  return { text: trimmed };
}

/** The badge tone for a confidence level. */
export function levelTone(level: string): "success" | "warning" | "neutral" {
  if (level === "high") {
    return "success";
  }
  if (level === "medium") {
    return "warning";
  }
  return "neutral";
}

/** "vpn, wi-fi,  network" → ["vpn", "wi-fi", "network"], blanks and repeats dropped. */
export function splitList(text: string): string[] {
  const seen = new Map<string, string>();
  for (const part of text.split(/[,;\n]/)) {
    const word = part.trim().replace(/\s+/g, " ");
    if (word !== "" && !seen.has(word.toLowerCase())) {
      seen.set(word.toLowerCase(), word);
    }
  }
  return [...seen.values()];
}

export interface PersonLike {
  name: string;
  email?: string | null;
  rec_id?: string | null;
}

/**
 * Names typed in the rule form back into people, keeping the email and RecId
 * already found for a name that did not change.
 */
export function namesToPeople(text: string, known: readonly PersonLike[]): PersonLike[] {
  const byName = new Map(known.map((p) => [p.name.trim().toLowerCase(), p]));
  return splitList(text).map((name) => byName.get(name.toLowerCase()) ?? { name });
}

export interface RuleLike {
  label: string;
  team: string;
  service?: string;
  category?: string;
  offering?: string;
  keywords?: readonly string[];
  owners?: readonly PersonLike[];
  backups?: readonly PersonLike[];
}

/** Whether a rule fits the register page's filter text (any of its words, any case). */
export function ruleMatchesFilter(rule: RuleLike, filter: string): boolean {
  const needle = filter.trim().toLowerCase();
  if (needle === "") {
    return true;
  }
  const hay = [
    rule.label,
    rule.team,
    rule.service ?? "",
    rule.category ?? "",
    rule.offering ?? "",
    ...(rule.keywords ?? []),
    ...(rule.owners ?? []).map((p) => p.name),
    ...(rule.backups ?? []).map((p) => p.name),
  ]
    .join(" ")
    .toLowerCase();
  return needle.split(/\s+/).every((word) => hay.includes(word));
}

/** What a rule matches on, for its one-line summary: "Service › Category", offering or keywords. */
export function ruleMatchSummary(rule: RuleLike): string {
  if (rule.offering) {
    return rule.offering;
  }
  const typed = [rule.service, rule.category].filter((v) => v !== undefined && v !== "");
  if (typed.length > 0) {
    return typed.join(" › ");
  }
  return (rule.keywords ?? []).join(", ");
}
